import { cubicInOut, cubicOut } from 'svelte/easing';
import { interpolate } from 'd3-interpolate';

import type { TransitionConfig } from 'svelte/transition';

type CustomSlideParams = {
  duration?: number;
  axis?: 'y' | 'x';
  from?: number;
  to?: number;
  easing?: (t: number) => number;
};

type CustomSlideTransition = (node: Element, params?: CustomSlideParams) => TransitionConfig;

export const customSlide: CustomSlideTransition = (
  _node,
  { duration = 300, axis = 'y', from = -100, to = 0, easing = cubicInOut } = {}
) => {
  return {
    duration,
    css: (t) => {
      const eased = easing(t);
      const interp = interpolate(from, to)(eased);

      return `opacity:${eased}; transform: translate${axis.toUpperCase()}(${interp}%);`;
    }
  };
};

export const zoomOnScrollHandler = (ref: HTMLElement, onChange: (n: number) => void) => {
  const screenH = window.innerHeight;
  const top = -screenH + ref.getBoundingClientRect().top + ref.getBoundingClientRect().height;
  if (top < 0 && top >= -screenH) {
    onChange(interpolate(1, 1.1)(-top / screenH));
  }
};

type FlyAndScaleParams = {
  y?: number;
  x?: number;
  start?: number;
  duration?: number;
};

// from: https://github.com/huntabyte/shadcn-svelte/blob/4e4dd6fef249fdc17e046c2c7a441a48fb64aeef/apps/www/src/lib/utils.ts#L129
export const flyAndScale = (
  node: Element,
  params: FlyAndScaleParams = { y: 0, x: 0, start: 0.95, duration: 150 }
): TransitionConfig => {
  const style = getComputedStyle(node);
  const transform = style.transform === 'none' ? '' : style.transform;

  const scaleConversion = (valueA: number, scaleA: [number, number], scaleB: [number, number]) => {
    const [minA, maxA] = scaleA;
    const [minB, maxB] = scaleB;

    const percentage = (valueA - minA) / (maxA - minA);
    const valueB = percentage * (maxB - minB) + minB;

    return valueB;
  };

  const styleToString = (style: Record<string, number | string | undefined>): string => {
    return Object.keys(style).reduce((str, key) => {
      if (style[key] === undefined) return str;
      return str + `${key}:${style[key]};`;
    }, '');
  };

  return {
    duration: params.duration ?? 200,
    delay: 0,
    css: (t) => {
      const y = scaleConversion(t, [0, 1], [params.y ?? 5, 0]);
      const x = scaleConversion(t, [0, 1], [params.x ?? 0, 0]);
      const scale = scaleConversion(t, [0, 1], [params.start ?? 0.95, 1]);

      return styleToString({
        transform: `${transform} translate3d(${x}px, ${y}px, 0) scale(${scale})`,
        opacity: t
      });
    },
    easing: cubicOut
  };
};
